import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Shield, TriangleAlert as AlertTriangle, CreditCard, Server, Ban, Scale, RefreshCw, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export const TermsOfServicePage = () => {
  const lastUpdated = 'January 2025';

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-foreground relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-white/70">
            The agreement between you and 961Host
          </p>
          <p className="text-white/40 mt-6 text-sm">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Welcome to 961Host. These Terms of Service govern your use of our web hosting services. 
            By creating an account or using our services, you acknowledge that you have read, understood, 
            and agree to be bound by these Terms.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Section 1 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">1. Acceptance of Terms</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-border">
              <p className="text-muted-foreground">
                By accessing or using 961Host's web hosting services, you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use our services. These terms apply to all users, 
                including customers, visitors, and anyone who accesses our platform.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">2. Services Description</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Web Hosting', desc: 'Server space for hosting websites and web applications' },
                { title: 'Domain Services', desc: 'Domain registration assistance and DNS management' },
                { title: 'Email Hosting', desc: 'Email accounts associated with your domain' },
                { title: 'Control Panel', desc: 'cPanel access for managing your hosting account' },
                { title: 'Technical Support', desc: 'Customer support via our contact channels' },
                { title: 'SSL Certificates', desc: "Free Let's Encrypt SSL certificates" },
              ].map((item, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-white border border-border">
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">3. Billing and Payment</h2>
            </div>
            <div className="space-y-4">
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-3">Payment Terms</h3>
                <ul className="space-y-2">
                  {[
                    'All fees are due in advance according to your billing cycle',
                    'Prices are in USD unless otherwise specified',
                    'We accept major credit cards and other payment methods as displayed',
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-3 text-muted-foreground">
                      <Check className="w-5 h-5 text-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-3">Renewals & Refunds</h3>
                <ul className="space-y-2">
                  {[
                    'Services automatically renew unless cancelled before the renewal date',
                    'Refund requests may be considered within 30 days of initial purchase',
                    'Domain registration fees are non-refundable once registered',
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-3 text-muted-foreground">
                      <Check className="w-5 h-5 text-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Section 4 - Acceptable Use */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Ban className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">4. Acceptable Use Policy</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-red-200">
              <p className="text-muted-foreground mb-4">You agree NOT to use our services for:</p>
              
              <div className="mb-6">
                <h3 className="font-semibold text-foreground mb-3">Prohibited Content</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    'Illegal content or activities',
                    'Copyright infringement',
                    'Malware distribution',
                    'Phishing or scams',
                    'Hate speech or harassment',
                    'Unauthorized adult content',
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-2 text-muted-foreground text-sm">
                      <X className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-3">Prohibited Activities</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    'Unauthorized system access',
                    'Sending spam emails',
                    'Cryptocurrency mining',
                    'DDoS attacks',
                    'Server abuse',
                    'Resource hogging',
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-2 text-muted-foreground text-sm">
                      <X className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-sm text-red-600 mt-4 pt-4 border-t border-red-100">
                Violation of this policy may result in immediate suspension or termination without refund.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">5. Account Security</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-3">Your Responsibilities</h3>
                <ul className="space-y-2">
                  {[
                    'Maintain account credential security',
                    'Use strong, unique passwords',
                    'Keep contact information updated',
                    'Report unauthorized access immediately',
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-3">Our Responsibilities</h3>
                <ul className="space-y-2">
                  {[
                    'Implement security measures',
                    'Protect server infrastructure',
                    'Monitor for security threats',
                    'Provide secure service access',
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Section 6 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">6. Service Level</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-border space-y-4">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Uptime Commitment</h3>
                <p className="text-muted-foreground text-sm">
                  We strive to maintain 99.9% server uptime. This excludes scheduled maintenance, 
                  circumstances beyond our control, and issues caused by customer actions.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Resource Limits</h3>
                <p className="text-muted-foreground text-sm">
                  Shared hosting accounts have resource limits to ensure fair usage. Accounts exceeding 
                  limits may be asked to upgrade or optimize.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Backup Responsibility</h3>
                <p className="text-muted-foreground text-sm">
                  While we perform regular backups, you are responsible for maintaining your own backups. 
                  Always keep local copies of important data.
                </p>
              </div>
            </div>
          </div>

          {/* Section 7 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Scale className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">7. Limitation of Liability</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-border">
              <p className="text-muted-foreground mb-4">
                Our services are provided "as is" without warranties of any kind. To the maximum extent 
                permitted by law, 961Host shall not be liable for:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  'Indirect, incidental, or consequential damages',
                  'Loss of profits, data, or business opportunities',
                  'Service interruptions or data loss',
                  'Actions of third parties',
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground border-t border-border pt-4">
                Our total liability shall not exceed the fees paid by you in the twelve (12) months preceding the claim.
              </p>
            </div>
          </div>

          {/* Section 8 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">8. Termination</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-3">By You</h3>
                <ul className="space-y-2">
                  {[
                    'Cancel anytime via support channels',
                    'Takes effect at billing period end',
                    'Backup your data before cancellation',
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-xl bg-white border border-border">
                <h3 className="font-semibold text-foreground mb-3">By Us</h3>
                <ul className="space-y-2">
                  {[
                    'Suspend accounts violating terms',
                    'Terminate for non-payment',
                    'Notice provided when reasonable',
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Section 9 */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Scale className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">9. Governing Law</h2>
            </div>
            <div className="p-6 rounded-xl bg-white border border-border">
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with the laws of Lebanon. 
                Any disputes arising from these Terms or your use of our services shall be subject to 
                the exclusive jurisdiction of the courts located in Beirut, Lebanon.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-3">
            Questions About Our Terms?
          </h2>
          <p className="text-muted-foreground mb-6">
            If you have any questions about these Terms of Service, please contact us.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors font-medium"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};
